/**
 * Care Invitation Service
 * Sends SMS and/or email when inviting family or caregivers to a care circle.
 */
import type { CareCircleMember } from '../types/care';
import { smsService } from './smsService';
import emailService from './emailService';

const INVITE_BASE_URL = 'https://bookapt.io';

export interface SendInviteParams {
  recipientId: string;
  recipientName: string;
  inviterName: string;
  member: CareCircleMember;
  baseUrl?: string;
}

/**
 * Build invite URL for accept flow.
 * Format: {baseUrl}/care-invite?recipientId=...&memberId=...&token=...
 */
export function buildInviteUrl(
  recipientId: string,
  memberId: string,
  token: string,
  baseUrl: string = INVITE_BASE_URL
): string {
  const params = new URLSearchParams({
    recipientId,
    memberId,
    token,
  });
  return `${baseUrl.replace(/\/$/, '')}/care-invite?${params.toString()}`;
}

/**
 * Generate SMS message for care invite.
 */
function buildSMSMessage(params: SendInviteParams, inviteUrl: string): string {
  const { recipientName, inviterName, member } = params;
  const roleLabel = member.role === 'coordinator' ? 'coordinator' : member.role === 'caregiver' ? 'caregiver' : 'family member';
  return `Hi ${member.name}! ${inviterName} invited you to join ${recipientName}'s care team on BookAPT as a ${roleLabel}. View meds, tasks & appointments. Accept: ${inviteUrl}`;
}

/**
 * Generate HTML email for care invite.
 */
function buildEmailHtml(params: SendInviteParams, inviteUrl: string): string {
  const { recipientName, inviterName, member } = params;
  const roleLabel = member.role === 'coordinator' ? 'Coordinator' : member.role === 'caregiver' ? 'Caregiver' : 'Family member';
  return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: #007AFF; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
      <h1 style="margin: 0;">Care Team Invitation</h1>
    </div>
    <div style="padding: 30px 20px; background: #f9f9f9; border: 1px solid #E5E5E7; border-top: none; border-radius: 0 0 8px 8px;">
      <p>Hi ${member.name},</p>
      <p><strong>${inviterName}</strong> has invited you to join <strong>${recipientName}'s</strong> care team on BookAPT as a <strong>${roleLabel}</strong>.</p>
      <p>You'll be able to:</p>
      <ul>
        <li>View medications and compliance</li>
        <li>See tasks and appointments</li>
        <li>Add notes and track incidents</li>
      </ul>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${inviteUrl}" style="background: #007AFF; color: white; padding: 14px 28px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold;">Accept invitation</a>
      </div>
      <p style="color: #666; font-size: 14px;">Or copy this link: ${inviteUrl}</p>
      <p style="color: #666; font-size: 12px; margin-top: 24px;">BookAPT – Care coordination</p>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Generate plain text email for care invite.
 */
function buildEmailText(params: SendInviteParams, inviteUrl: string): string {
  const { recipientName, inviterName, member } = params;
  const roleLabel = member.role === 'coordinator' ? 'Coordinator' : member.role === 'caregiver' ? 'Caregiver' : 'Family member';
  return `Hi ${member.name},

${inviterName} has invited you to join ${recipientName}'s care team on BookAPT as a ${roleLabel}.

You'll be able to view medications, tasks, appointments, and add notes.

Accept invitation: ${inviteUrl}

BookAPT – Care coordination`;
}

export interface SendInviteResult {
  smsSent: boolean;
  emailSent: boolean;
  inviteUrl: string;
  error?: string;
}

/**
 * Send care team invite via SMS and/or email.
 * Uses smsService for SMS; emailService for email (may fail if not initialized).
 */
export async function sendCareInvite(params: SendInviteParams): Promise<SendInviteResult> {
  const { recipientId, recipientName, inviterName, member } = params;
  const token = member.inviteToken;
  if (!token) {
    return {
      smsSent: false,
      emailSent: false,
      inviteUrl: '',
      error: 'Missing invite token on member',
    };
  }

  const baseUrl = params.baseUrl ?? INVITE_BASE_URL;
  const inviteUrl = buildInviteUrl(recipientId, member.id, token, baseUrl);

  let smsSent = false;
  let emailSent = false;

  // Send SMS if phone is available
  if (member.phone && member.phone.trim()) {
    try {
      const smsMessage = buildSMSMessage(params, inviteUrl);
      smsSent = await smsService.sendSMS(member.phone.trim(), smsMessage, 'general');
    } catch (e) {
      console.warn('Care invite SMS error:', e);
    }
  }

  // Send email if email is available
  if (member.email && member.email.trim()) {
    try {
      const subject = `${inviterName} invited you to ${recipientName}'s care team on BookAPT`;
      const html = buildEmailHtml(params, inviteUrl);
      const text = buildEmailText(params, inviteUrl);
      const result = await emailService.sendEmail({
        to: member.email.trim(),
        subject,
        html,
        text,
      });
      emailSent = result.success;
      if (!result.success) {
        console.warn('Care invite email error:', result.error);
      }
    } catch (e) {
      console.warn('Care invite email error:', e);
    }
  }

  return {
    smsSent,
    emailSent,
    inviteUrl,
  };
}
